import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTable } from '../table/Table';
import Input from '../ui/Input';
import Button from '../ui/Button';

const EditModal = ({ defaultValue, close, onEdit, offsetContainer }) => {
  const { status } = useTable();
  const [editValue, setEditValue] = useState(defaultValue);
  const editValueRef = useRef(defaultValue);
  const modal = useRef();

  const handleInputChange = (e) => {
    setEditValue(e.target.value);
    editValueRef.current = e.target.value;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onEdit(editValueRef.current);
    // After submitting, check if status.edit is "idle" and then call close()
    if (status.edit === "idle") {
      close();
    }
  };

  // Sync editValue with defaultValue whenever defaultValue changes
  useEffect(() => {
    setEditValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    // Calculate so that modal is open above the ref (offsetContainer)
    const { top, left, height } = offsetContainer.getBoundingClientRect();
    const modalHeight = modal.current.getBoundingClientRect().height;
    const modalWidth = modal.current.getBoundingClientRect().width;

    // Position the modal above the ref, with a small gap (e.g., 4px)
    modal.current.style.top = `${window.scrollY + top - modalHeight - 4}px`;
    modal.current.style.left = `${left}px`;

    // If not enough space above, fallback to below (optional, for safety)
    if (top - modalHeight - 4 < 0) {
      modal.current.style.top = `${window.scrollY + top + height + 4}px`;
    }

    // Adjust horizontal position if needed
    if (left < modalWidth) {
      modal.current.style.translate = "30px 0px";
    } else {
      modal.current.style.translate = "calc(-100% - 4px) 0px";
    }
  }, []);

  // To ensure this modal is always on top of any other modal, use a very high z-index (e.g., z-[9999])
  // and also render it directly to document.body (not inside #root, which may be under other modals).
  return createPortal(
    <>
      <div onClick={close} className="fixed inset-0 z-[9999]"></div>

      <div
        ref={modal}
        className="absolute bg-white border px-3 pb-3 flex flex-col items-end z-[99999]"
        style={{
          zIndex: 99999,
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
        }}
      >
        <div className="h-[52px] flex items-center w-full justify-between">
          <h6 className="text-xl font-semibold">Edit Cell</h6>

          <button
            onClick={close}
            className="border size-8 flex justify-center items-center rounded-md"
          >
            <img src="/cross.svg" alt="close" className="size-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <Input
            value={editValue}
            onChange={handleInputChange}
            placeholder="Enter value here"
            className="!h-11"
          />

          <div className="flex gap-2 self-end">
            <Button
              type="button"
              onClick={close}
              className="bg-transparent !text-cherry"
            >
              Cancel
            </Button>

            <Button
              type="submit"
              state={status.edit}
              disabled={
                (editValue === "") ||
                editValue === defaultValue ||
                status.edit === "loading" 
              }
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body // Use document.body to ensure it's above all other modals
  );
};

const VoterDetailsModal = ({ 
  isOpen, 
  isShowDot,
  onClose, 
  data, 
  title = "Details",
  fields = [],
  headerField = null,
  onOpenModal,
  Overlay,
  classWrapper,
  modalClass,
  specialFields = {} // For fields that need special rendering like phone with icons
}) => {
  const editBtn = useRef();
  const { status,onEdit } = useTable();
  const [isEditing, setIsEditing] = useState(false);
  // Prevent scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    // Cleanup function
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Determine which field to show in the header
  const headerValue = headerField && data[headerField] ? data[headerField] : null;

  // Render special field with custom UI (like phone with icons)
  const renderSpecialField = (fieldInfo, value) => {
    const specialRender = specialFields[fieldInfo.key];
    
    if (specialRender && typeof specialRender === 'function') {
      return specialRender(value);
    }
    
    return <span className="font-medium">{value}</span>;
  };

  return createPortal(
    <div className={`fixed inset-0 flex justify-center z-50 ${classWrapper}`}>
      {/* Overlay */}
      {
        Overlay ? 
          <div 
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={onClose}
          ></div>
        : null
      }
      
      {/* Modal content */}
      <div className={`bg-white shadow-lg z-10 ${modalClass}`}>
        <div className="flex justify-between items-center xxs:p-[20px] p-[15px] border-b border-[#EAEDF2]">
          <h2 className="text-[20px] font-medium leading-[26px] tracking-normal font-mont">{title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <img src='/cross.svg' className='w-[16px] h-[16px]' />
          </button>
        </div>
        
        <div className="xxs:p-[20px] p-[15px]">
          {headerValue && (
            <div className="flex justify-between items-center p-3 bg-gray-100 rounded mb-2">
              <span className="font-medium">{headerValue}</span>
              <button className="text-gray-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"></path>
                </svg>
              </button>
            </div>
          )}
          
          <div className="max-h-[700px] overflow-auto">
            {fields.map((field, index) => {
              // Skip the header field if it's already shown above
              if (field.key === headerField) return null;
              
              // Handle array data differently (like voting history)
              if (Array.isArray(data[field.key])) {
                return (
                  <div key={index} className="py-2 border-b">
                    <div>{field.label}</div>
                    <div className="flex justify-between mt-1">
                      {data[field.key].map((item, idx) => (
                        <span key={idx} className="font-medium">{item}</span>
                      ))}
                    </div>
                  </div>
                );
              }
              
              // Standard field rendering
              return (
                <div key={index} className={`${field.label ? 'flex justify-between' : ''} ${field.key === 'first_name' || field.key === 'full_name' ? 'bg-[#E5E7EB]' : ''} flex-wrap p-[10px] border-b border-[#EAEDF2] gap-2`}>
                  {field.label ? field.key === 'first_name' || field.key === 'full_name' ? <span className='text-[20px] font-medium leading-[26px] tracking-normal'>{data[field.key]}</span>  : <span className='text-[16px] font-medium leading-[26px] tracking-normal font-mont pr-[10px]' title={field.label}>{field.label}</span> : null}
                  {field.key in specialFields 
                    ? renderSpecialField(field, field.key === 'phone_number' ? { phone: data[field.key], email: data['email'] } : data[field.key])
                    : 
                      field.key === 'first_name' || field.key === 'full_name' ? 
                        <span className="">
                          {isShowDot ? <img className='cursor-pointer' src='/dots.svg' alt="dots" onClick={onOpenModal}  /> : null}
                        </span> :
                        field.key === 'user_email' ? 
                          <>
                          <div className='flex'>
                              <a href={`mailto:${data[field.key]}`} className="underline">
                                  {data[field.key]}
                              </a>
                              <img src="/edit.svg" alt="" className="size-[18px] mt-1 ml-1 cursor-pointer" ref={editBtn}
                                    onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIsEditing((prev) => !prev);
                                    }}
                                />
                          </div>
                          </>
                           :
                        <span className="text-[16px] font-normal leading-[26px] tracking-normal font-mont text-lightBlack" title={data[field.key] ? data[field.key] : '-'}>{data[field.key] ? data[field.key] : '-'}</span>
                  }
                {isEditing && (
                  <EditModal
                    key={data[field.key]} // force remount on value change
                    defaultValue={data[field.key]}
                    close={() => setIsEditing(false)}
                    onEdit={(value) => onEdit(data, {key:'user_email'}, value)}
                    offsetContainer={editBtn.current}
                />
                )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default VoterDetailsModal;